import React, { useState, useEffect } from 'react';
import {
  Table,
  Alert,
  Row,
  Col,
  Button,
  DropdownToggle,
  UncontrolledDropdown,
  DropdownMenu,
  DropdownItem,
  UncontrolledTooltip,
} from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { Link, useHistory } from 'react-router-dom';
import moment from 'moment/min/moment-with-locales';
import * as utils from '../../Services/utils';
import JobStatusButtons from '../shared/JobButtons';
import '../shared/dropdown.css';
import AssignedUserModal from '../../Components/Modals/assignedUserModal';
import CompetenceTags from '../shared/competenceTags';
import { basicDateFormat } from '../../Helpers/dateTimeHelper';
import { JobStatus } from '../../Helpers/enums';
import { closeJob } from '../../Helpers/jobHelper';
import ConfigureEmailModal from '../Modals/configureEmailModal';
import { getParsedEmailSetting, updateCompanyEmailSettingsLocal } from '../../Helpers/companyHelper';
import { AutomatedEmailSending } from '../../Helpers/enums';
import { AssignedUserType } from '../../Components/Modals/assignedUserModal';
import { hasAdminRole } from '../../Services/utils';
import SearchNewApplicantsModal from '../Modals/SearchNewApplicantsModal';
import useCompanyStore from '../../../../stores/companyStore';
import useAuthStore from '../../../../stores/authStore';
import copy from 'copy-to-clipboard';

const JobsTable = ({ jobs, counting, refreshStatus }) => {
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const [showModal, setShowModal] = useState(false);
  const [showSearchUsersModal, setShowSearchUsersModal] = useState(false);
  const [job, setJob] = useState({});
  const [isConfigureEmailModalOpen, setIsConfigureEmailModalOpen] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const user = useAuthStore((state) => state.user);
  const company = useCompanyStore((state) => state.company);
  const fetchCompany = useCompanyStore((state) => state.fetchCompany);
  const isAdmin = hasAdminRole(user);

  useEffect(() => {
    const affectedEmail = getParsedEmailSetting(
      AutomatedEmailSending.SendEmailToAllNewApplicantsWhenJobClosesOrArchives,
      company
    );
    setSelectedEmail({
      id: AutomatedEmailSending.SendEmailToAllNewApplicantsWhenJobClosesOrArchives,
      emailSubject: affectedEmail?.EmailSubject,
      emailContent: affectedEmail?.EmailContent,
    });
  }, []);

  const viewJobDetails = (id) => {
    const job = jobs.find((x) => x.id === id);
    let url = '';

    if (job) {
      /*global WEB_PORTAL_URL */
      url =
        job.jobStatus === JobStatus.Published
          ? `${WEB_PORTAL_URL}/job-details/${id}`
          : `${WEB_PORTAL_URL}/job-details-preview/${id}`;

      window.open(url, '_blank').focus();
    }
  };

  const editJobDetails = (id) => {
    history.push(`/jobs/${id}`);
  };

  const goToApplicationCreation = (id) => {
    const url = `${window.location.origin}/applicationCreation/${id}`;
    window.open(url, '_blank').focus(); // Opens the link in a new tab
  };

  const handleExpirationDate = (date) => {
    const today = moment();
    const diffFromToday = moment.utc(date) - today;
    const days = moment.duration(diffFromToday).asDays();

    if (diffFromToday < 0) {
      return (
        <div className="text-danger">
          <b>
            <em className="fa fas fa-exclamation-circle"></em>
            &nbsp;{t('jobs.EXPIRED')} {moment.utc(date).fromNow()}
          </b>
          {/* <p className="text-muted">{t('jobs.EXPIRES_MESSAGE')}</p> */}
        </div>
      );
    } else {
      return (
        <div className={days < 6 ? 'text-warning' : 'text-muted'}>
          {t('jobs.EXPIRES')} ({moment.utc(date).fromNow()})
        </div>
      );
    }
  };

  const toggleAssignedUserModal = (job) => {
    setShowModal(!showModal);
    setJob(job);
  };

  const toggleSearchUserModal = () => {
    setShowSearchUsersModal(!showSearchUsersModal);
  };

  const openSearchUserModal = (job) => {
    setJob(job);
    setShowSearchUsersModal(true);
  };

  const toggleIsConfigureEmailModalOpen = () => {
    setIsConfigureEmailModalOpen(!isConfigureEmailModalOpen);
  };

  const composeUrl = (jobId) => {
    return window.location.origin + `/applicationCreation/${jobId}`;
  };

  const actionIcons = () => {
    return (
      <div className="float-right">
        <UncontrolledDropdown>
          <DropdownToggle color="link" className="p-0 text-muted">
            <em className="fa fa-ellipsis-h fa-lg"></em>
          </DropdownToggle>
          <DropdownMenu className="dropdown-menu-right-forced animated fadeInLeft">
            <DropdownItem>
              <em className="fa-fw fa fa-plus mr-2"></em>
              <span>New contact</span>
            </DropdownItem>
            <DropdownItem>
              <em className="fa-fw fas fa-share mr-2"></em>
              <span>Import contact</span>
            </DropdownItem>
            <DropdownItem>
              <em className="fa-fw fas fa-reply mr-2"></em>
              <span>Export contacts</span>
            </DropdownItem>
          </DropdownMenu>
        </UncontrolledDropdown>
      </div>
    );
  };

  return (
    <div>
      {jobs.length > 0 ? (
        <>
          <SearchNewApplicantsModal
            isModalOpen={showSearchUsersModal}
            toggleIsModalOpen={toggleSearchUserModal}
            job={job}
          />
          <ConfigureEmailModal
            isEmailModalOpen={isConfigureEmailModalOpen}
            toggleIsEmailModalOpen={toggleIsConfigureEmailModalOpen}
            selectedColumn={selectedEmail}
            updateEmailSettings={(id, sendAutomatedEmail, emailSubject, emailContent) =>
              updateCompanyEmailSettingsLocal(
                company?.id,
                id,
                sendAutomatedEmail,
                emailSubject,
                emailContent,
                fetchCompany
              )
            }
            hideAutomatedSendingOption={true}
          />
          <AssignedUserModal
            showModal={showModal}
            onToggle={toggleAssignedUserModal}
            onStatusRefresh={refreshStatus}
            job={job}
            assignedUserType={AssignedUserType.AssignedToJob}
          />
          <div style={{ overflowX: 'auto', width: '100%', minHeight: '250px' }}>
            <Table striped bordered style={{ minWidth: '1200px' }}>
              <thead>
                <tr>
                  <th></th>
                  <th hidden>UID</th>
                  <th>{t('common.TITLE')}</th>
                  <th>{t('common.STATUS')}</th>
                  {/* <th>
                    {t('common.TYPE')}
                    <span className="text-muted pl-1">
                      <InfoTag id="allJobs" lng={i18n.language} />{' '}
                    </span>
                  </th> */}
                  <th>{t('jobs.EMPLOYMENT_TYPE')}</th>
                  <th>{t('jobs.EXPERIENCE')}</th>
                  <th>{t('jobs.FIELD')}</th>
                  <th>{t('competences.COMPETENCES')}</th>
                  <th>{t('applications.APPLICATIONS')}</th>
                  <th>{t('common.ADDED')}</th>
                  <th>{t('common.MODIFIED')}</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {jobs.map((job, index) => (
                  <tr key={index}>
                    <td style={{ fontSize: 'large' }}>{counting + index + 1}</td>
                    <td hidden>{job.id}</td>
                    <td>
                      <Row>
                        <Col>
                          <h6 className="m-0 text-bold">{job.title}</h6>
                          {isAdmin && <h6 className="text-muted">{job.companyName}</h6>}
                        </Col>
                      </Row>
                      {job.jobStatus === JobStatus.Published && (
                        <>
                          <Row>
                            <Col>
                              <div>
                                {t('applications.APPLICATION_URL')}
                                &nbsp;
                                <Link to={`/applicationCreation/${job.id}`}>{composeUrl(job.id)}</Link>
                                &nbsp;
                                <em
                                  id={`copyToClipboardInfo-${index}`}
                                  style={{ cursor: 'pointer' }}
                                  className="fa mr-2 fas fa-copy"
                                  onClick={() => copy(composeUrl(job.id))}
                                />
                                <UncontrolledTooltip
                                  delay={{ show: 300, hide: 0 }}
                                  placement="left"
                                  target={`copyToClipboardInfo-${index}`}
                                >
                                  {t('jobs.COPY_TO_CLIPBOARD')}
                                </UncontrolledTooltip>
                              </div>
                            </Col>
                          </Row>
                          <Row>
                            <Col>{handleExpirationDate(job.expirationDate)}</Col>
                          </Row>
                        </>
                      )}
                      <Row>
                        <Col>
                          {t('jobs.ASSIGNED_TO')}
                          <Button color="primary" onClick={() => toggleAssignedUserModal(job)} outline size="xs">
                            <strong>{job.assignedTo}</strong>
                          </Button>
                        </Col>
                      </Row>
                    </td>
                    <td style={{ width: '120px' }}>
                      <JobStatusButtons
                        job={job}
                        isDropDown={true}
                        onStatusRefresh={(status) => refreshStatus(job, status)}
                        language={i18n.language}
                      />
                    </td>
                    {/* <td>
                      <div id={`jobType-${index}`} className={utils.getBadgeColor(job.jobType)}>
                        {utils.translateJobType(job.jobType)}
                      </div>
                      <UncontrolledTooltip delay={{ show: 300, hide: 0 }} placement="right" target={`jobType-${index}`}>
                        {job.jobType === 'Open'
                          ? HelpMessages.getOpenJobsText(i18n.language)
                          : HelpMessages.getBoundedJobsText(i18n.language)}
                      </UncontrolledTooltip>
                    </td> */}
                    <td>{utils.translateEmploymentType(job.employmentType)}</td>
                    <td>{utils.translateExperienceType(job.experience)}</td>
                    <td style={{ width: '120px' }}>{job.field}</td>
                    <td style={{ width: '240px' }}>
                      <CompetenceTags competences={job.jobCompetences} />
                    </td>
                    <td className="text-center">
                      {job.numberOfApplicants > 0 ? (
                        <Link to={`/applications?jobId=${job.id}`}>
                          <em className="fa fas fa-envelope-open-text far icon-hover-effect-no-color">
                            {' '}
                            {job.numberOfApplicants}
                          </em>
                        </Link>
                      ) : (
                        <em className="fa fas fa-envelope-open-text"> {job.numberOfApplicants}</em>
                      )}
                      <div>
                        <em className="fa mt-1 fas fa-eye"> {job.numberOfDetailedViews + job.numberOfViews}</em>
                      </div>
                    </td>
                    <td style={{ width: '120px' }}>
                      {moment.utc(job.created).format(basicDateFormat)}, {moment.utc(job.created).fromNow()}
                    </td>
                    <td>{moment.utc(job.modified).fromNow()}</td>
                    <td className="text-center">
                      <div>
                        <UncontrolledDropdown>
                          <DropdownToggle color="link" className="p-0 text-muted">
                            <em className="fa-2x fas fa-ellipsis-h far icon-hover-effect-no-color"></em>
                          </DropdownToggle>
                          <DropdownMenu className="dropdown-menu-right-forced animated fadeInLeft">
                            <DropdownItem onClick={() => viewJobDetails(job.id)}>
                              <em className="fa-fw mr-2 fas fa-eye far"></em>
                              <span>{t('common.PREVIEW')}</span>
                            </DropdownItem>
                            <DropdownItem onClick={() => editJobDetails(job.id)}>
                              <em className="fa-fw mr-2 fas fa-pen far"></em>
                              <span>{t('common.EDIT')}</span>
                            </DropdownItem>
                            <DropdownItem onClick={() => goToApplicationCreation(job.id)}>
                              <em className="fa-fw mr-2 fas fa-sign-in-alt far"></em>
                              <span>{t('jobs.START_WIZ_FOR_JOBAPPLICATION')}</span>
                            </DropdownItem>
                            <DropdownItem onClick={() => openSearchUserModal(job)}>
                              <em className="fa-fw mr-2 fas fa-search-plus far"></em>
                              <span>Search candidates on GitHub</span>
                            </DropdownItem>
                            {job.jobStatus !== JobStatus.Deleted && (
                              <DropdownItem
                                onClick={() =>
                                  closeJob(
                                    job,
                                    t,
                                    () => refreshStatus(job, JobStatus.Deleted),
                                    toggleIsConfigureEmailModalOpen,
                                    JobStatus.Deleted
                                  )
                                }
                              >
                                <em className="fa-fw mr-2 fas fa-trash far"></em>
                                <span> {t('common.DELETE')}</span>
                              </DropdownItem>
                            )}
                          </DropdownMenu>
                        </UncontrolledDropdown>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </>
      ) : (
        <Alert color="success">
          <em id="noJobsYetInfo" className="fa-x px-2 icon-info">
            <b className="px-2">{t('jobs.NO_JOBS_YET_INFO')}</b>
          </em>
        </Alert>
      )}
    </div>
  );
};

export default JobsTable;
